import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
// import jwtDecode from 'jwt-decode';
import axios from 'axios';
import {
  bookingMetaData,
  buyerBookingDetailsColumns,
} from '../../schema/orderColumns';
import PanelCard from '../../Components/panel-card/panel-card';
import TitleDescriptionLoader from '../../Components/title-description/loader';
import TitleDescription from '../../Components/title-description/title-description';
import Skeleton from '../../Components/skeleton/skeleton';
import AccordionPanel from '../../Components/accordion-panel/accordion-panel';
import DetailsListingLoader from '../details-page/details-listing/loader';
import DetailsListing from '../details-page/details-listing/details-listing';
import CommentSection from '../../Components/comment-section/comment-section';
import { emailApprovalLiner } from '../../schema/bookingColumns';
import '../details-page/details-page.scss';
// import LinkExpired from '../../assets/image/link-expired.svg';
// import ThankYou from '../../assets/image/quote-thankyou.svg';
// import ActionPerformed from '../../assets/image/action-performed.svg';
import ActionStatus from '../../Components/action-status/action-status';
import {
  ExpairedActionIcon,
  PerformedActionIcon,
  SomethingWentWrongIcon,
  SuccessActionIcon,
} from '../../Components/icons/icons';
import { getUserLanguage } from '../../service/authService';

const baseHost = (() => {
  switch (process.env.REACT_APP_STAGE) {
    case 'prod':
      return 'api.quloi.com';
    case 'beta':
      return 'omni-beta-api.quloi.com';
    case 'demo':
      return 'demo-api.quloi.com';
    default:
      return 'omni-dev-api.quloi.com';
  }
})();

export const host = `https://${baseHost}/`;

function EmailQuoteApproval() {
  const [t] = useTranslation('managebooking');

  const [searchParams, setSearchParams] = useSearchParams();
  const tokenParam = searchParams.get('token');
  const langParam = searchParams.get('lang');
  const userSubParam = searchParams.get('userSub');
  const [linerColumn, setLinerColumn] = useState([]);
  const [quoteData, setQuoteData] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionApiLoading, setActionApiLoading] = useState(false);

  const fetchQuote = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${host}poAPI/v1/bookingService/getBookingDetails?token=${tokenParam}&lang=${getUserLanguage()}`
      );
      setQuoteData(res.data.data);
      setLoading(false);
    } catch (err) {
      if (err.response.status === 401) {
        setStatus('linkExpired');
      } else if (err.response.status === 422) {
        setStatus('actionTaken');
      } else {
        setStatus('error');
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('language')) {
      localStorage.setItem('language', langParam || 'en');
    } else if (langParam && getUserLanguage() !== langParam) {
      localStorage.setItem('language', getUserLanguage());
      searchParams.set('lang', getUserLanguage());
      setSearchParams(searchParams);
    }
    fetchQuote();
  }, [tokenParam]);

  const bookingDetails = quoteData?.[0];
  const bookingPackages = bookingDetails?.packages;
  const poNumber = bookingDetails?.poNumber;
  const comment = bookingDetails?.notes?.[0];

  // useEffect(() => {
  //   // if (token ) {
  //   // const decoded = jwtDecode(token); // you’ll get bookingId, forwarderId, etc.
  //   fetchQuote();
  //   // }
  // }, [tokenParam]);

  const handleAction = async (action) => {
    const url = bookingDetails?.postActionAPI;
    setActionApiLoading(true);
    try {
      await axios.post(
        url,
        {
          bookingId: bookingDetails?._id,
          status: action,
          quotationId: bookingDetails?.linerQuotations?.[0]?._id,
          userSub: userSubParam,
        },
        {
          headers: { Authorization: `${tokenParam}` },
        }
      );
      setStatus('success');
    } catch {
      setStatus('error');
    } finally {
      setActionApiLoading(false);
    }
  };

  useEffect(() => {
    const linerColumnData = emailApprovalLiner(t);
    if (!loading) setLinerColumn(linerColumnData);
  }, [loading]);

  if (status === 'success')
    return (
      <ActionStatus
        title={t('managebookingscreen.emailapproval.successtitle')}
        description={t('managebookingscreen.emailapproval.successdescription')}
      >
        <SuccessActionIcon />
      </ActionStatus>
    );
  if (status === 'linkExpired')
    return (
      <ActionStatus
        title={t('managebookingscreen.emailapproval.linkexpiredtitle')}
        description={t(
          'managebookingscreen.emailapproval.linkexpireddescription'
        )}
      >
        <ExpairedActionIcon />
      </ActionStatus>
    );
  if (status === 'actionTaken')
    return (
      <ActionStatus
        title={t('managebookingscreen.emailapproval.actionperformedtitle')}
        description={t(
          'managebookingscreen.emailapproval.actionperformeddescription'
        )}
      >
        <PerformedActionIcon />
      </ActionStatus>
    );
  if (status === 'error')
    return (
      <ActionStatus
        title={t('managebookingscreen.emailapproval.errortitle')}
        description={t('managebookingscreen.emailapproval.errordescription')}
      >
        <SomethingWentWrongIcon />
      </ActionStatus>
    );

  const displayAddressPanel =
    bookingDetails?.ConsigneeDetails ||
    bookingDetails?.ShipperDetails ||
    bookingDetails?.entities?.some((entity) => entity?.name);

  return (
    <div className="d-flex m-auto">
      <div className="details-container d-flex justify-center">
        <div className="details-left email-quote">
          <div className="details-body">
            <PanelCard>
              <div className="details-display">
                {!bookingDetails ? (
                  <TitleDescriptionLoader />
                ) : (
                  bookingMetaData?.map((data) => {
                    if (bookingDetails?.[data?.accessor] !== null) {
                      if (
                        data.accessor === 'quotationExpiryDate' ||
                        data.accessor === 'cargoReadyDate' ||
                        data.accessor === 'scheduledPickupDate' ||
                        data.accessor === 'scheduledPickupToDate'
                      ) {
                        const dateValue = bookingDetails?.[data?.accessor];
                        const formattedDate = dateValue
                          ? format(new Date(dateValue), 'dd/MM/yyyy') // Format to 'dd/MM/yyyy'
                          : 'N/A'; // Fallback for null or undefined values

                        return (
                          <TitleDescription
                            title={t(
                              `managebookingscreen.bookingdetails.overview.${data?.title}`
                            )}
                            description={formattedDate}
                          />
                        );
                      }

                      return (
                        <TitleDescription
                          title={t(
                            `managebookingscreen.bookingdetails.overview.${data?.title}`
                          )}
                          description={bookingDetails?.[data?.accessor]}
                        />
                      );
                    }
                    return '';
                  })
                )}
              </div>
            </PanelCard>

            {/* Address section */}
            {displayAddressPanel && (
              <AccordionPanel
                title={t('managebookingscreen.bookingdetails.address')}
              >
                <div className="details-display details-row">
                  {bookingDetails?.ConsigneeDetails && (
                    <div className="details-column">
                      {bookingDetails?.ConsigneeDetails ? (
                        <PanelCard
                          cardTitle={t(
                            'managebookingscreen.bookingdetails.buyer'
                          )}
                          border
                        >
                          <p className="org-name">
                            {bookingDetails?.ConsigneeDetails?.orgName}
                          </p>
                          {bookingDetails?.consigneeEntityDetails
                            ?.entityName && (
                            <p className="org-address">
                              {
                                bookingDetails?.consigneeEntityDetails
                                  ?.entityName
                              }
                            </p>
                          )}
                          {bookingDetails?.ConsigneeDetails?.entityERPValue
                            ?.ERPValue ? (
                            <p className="org-address">
                              {
                                bookingDetails?.ConsigneeDetails?.entityERPValue
                                  ?.ERPValue
                              }
                            </p>
                          ) : (
                            bookingDetails?.ConsigneeDetails?.orgERPValue
                              ?.ERPValue && (
                              <p className="org-address">
                                {
                                  bookingDetails?.ConsigneeDetails?.orgERPValue
                                    ?.ERPValue
                                }
                              </p>
                            )
                          )}
                          <p className="org-address">
                            {bookingDetails?.ConsigneeDetails?.orgAddress}
                          </p>
                        </PanelCard>
                      ) : (
                        <PanelCard
                          cardTitle={
                            <Skeleton
                              skWidth="6.944vw"
                              skHeight="0.833vw"
                              marginTop="0.417vw"
                              marginBottom="0.417vw"
                            />
                          }
                          border
                        >
                          <p className="org-name">
                            <Skeleton
                              skWidth="100%"
                              skHeight="0.694vw"
                              marginBottom="0.694vw"
                            />
                            <Skeleton skWidth="70%" skHeight="0.694vw" />
                          </p>
                        </PanelCard>
                      )}
                    </div>
                  )}
                  {bookingDetails?.ForwarderDetails && (
                    <div className="details-column">
                      {bookingDetails?.ForwarderDetails ? (
                        <PanelCard
                          cardTitle={t(
                            'managebookingscreen.bookingdetails.forwarder'
                          )}
                          border
                        >
                          <p className="org-name">
                            {bookingDetails?.ForwarderDetails?.orgName}
                          </p>
                          <p className="org-address">
                            {bookingDetails?.ForwarderDetails?.orgAddress}
                          </p>
                        </PanelCard>
                      ) : (
                        <PanelCard
                          cardTitle={
                            <Skeleton
                              skWidth="6.944vw"
                              skHeight="0.833vw"
                              marginTop="0.417vw"
                              marginBottom="0.417vw"
                            />
                          }
                          border
                        >
                          <p className="org-name">
                            <Skeleton
                              skWidth="100%"
                              skHeight="0.694vw"
                              marginBottom="0.694vw"
                            />
                            <Skeleton skWidth="70%" skHeight="0.694vw" />
                          </p>
                        </PanelCard>
                      )}
                    </div>
                  )}
                  {bookingDetails?.ShipperDetails?.orgName && (
                    <div className="details-column">
                      <PanelCard
                        cardTitle={t(
                          'managebookingscreen.bookingdetails.supplier'
                        )}
                        border
                      >
                        <p className="org-name">
                          {bookingDetails?.ShipperDetails?.orgName}
                        </p>
                        {bookingDetails?.shipperEntityDetails?.entityName && (
                          <p className="org-address">
                            {bookingDetails?.shipperEntityDetails?.entityName}
                          </p>
                        )}
                        {bookingDetails?.ShipperDetails?.entityERPValue
                          ?.ERPValue ? (
                          <p className="org-address">
                            {
                              bookingDetails?.ShipperDetails?.entityERPValue
                                ?.ERPValue
                            }
                          </p>
                        ) : (
                          bookingDetails?.ShipperDetails?.orgERPValue
                            ?.ERPValue && (
                            <p className="org-address">
                              {
                                bookingDetails?.ShipperDetails?.orgERPValue
                                  ?.ERPValue
                              }
                            </p>
                          )
                        )}
                        <p className="org-address">
                          {bookingDetails?.ShipperDetails?.orgAddress}
                        </p>
                      </PanelCard>
                    </div>
                  )}
                  {bookingDetails?.entities &&
                    bookingDetails?.entities.map((item) => {
                      return (
                        <>
                          {item?.name && (
                            <div className="details-column">
                              <PanelCard
                                cardTitle={t(
                                  `managebookingscreen.bookingdetails.${item?.type}`
                                )}
                                border
                              >
                                <p className="org-name">{item?.name}</p>
                                <p className="org-address" />
                              </PanelCard>
                            </div>
                          )}
                          <span />
                        </>
                      );
                    })}
                </div>
              </AccordionPanel>
            )}

            {/* Comment section */}
            {comment?.comment && (
              <AccordionPanel
                title={t('managebookingscreen.bookingdetails.bookingnote')}
              >
                <CommentSection commentData={comment} />
              </AccordionPanel>
            )}

            {bookingPackages?.length > 0 ? (
              <DetailsListing
                title={t('managebookingscreen.bookingdetails.packagedetails')}
                data={bookingPackages?.map((pack) => ({
                  ...pack,
                  poNumber,
                }))}
                columns={buyerBookingDetailsColumns(
                  null,
                  false,
                  true,
                  bookingDetails?.bookingType,
                  true // isEmailApproval
                )}
                isCheckBoxRequired={false}
              />
            ) : (
              <DetailsListingLoader />
            )}

            {!loading && quoteData?.length > 0 ? (
              <DetailsListing
                title={t(
                  'managebookingscreen.bookingdetails.linerquotes.title'
                )}
                data={bookingDetails?.linerQuotations}
                columns={linerColumn}
                activeEmailApproval={true}
                handleEmailApproval={handleAction}
                disableEmailApproval={actionApiLoading}
              />
            ) : (
              <DetailsListingLoader />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailQuoteApproval;
